
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  loadLeagueData, 
  saveLeagueData, 
  resetWeek, 
  resetAllData, 
  getNextMatchDate,
  weekSchedule,
} from '../../utils/localStorage';
import type { LeagueData, Player } from '../../utils/localStorage';

const RESULTS_STORAGE_KEY = 'mbc_results_data';

interface IndividualResult {
  place: '1st' | '2nd' | '3rd';
  playerId: string;
  playerName: string;
  netScore: number | string;
  totalIndividualPoints: number | string;
}

interface TeamResult {
  place: '1st' | '2nd';
  player1Id: string;
  player1Name: string;
  player2Id: string;
  player2Name: string;
  player1Score: number | string;
  player2Score: number | string;
  teamTotalScore: number | string;
  teamTotalPoints: number | string;
}

interface RoundResults {
  individuals: IndividualResult[];
  teams: TeamResult[];
}

function loadResultsData(): Record<string, RoundResults> {
  try {
    const stored = localStorage.getItem(RESULTS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Error loading results data:', e);
  }
  return {};
}

function saveResultsData(data: Record<string, RoundResults>) {
  localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify(data));
}

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<'individual' | 'pairings'>('individual');
  const [scheduleFilter, setScheduleFilter] = useState<'upcoming' | 'completed'>('upcoming');
  const [showScoreInput, setShowScoreInput] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [passcode, setPasscode] = useState('');
  const [isPasscodeVerified, setIsPasscodeVerified] = useState(false);
  const [grossScore, setGrossScore] = useState('');
  const [netScore, setNetScore] = useState('');
  const [timeToNextMatch, setTimeToNextMatch] = useState('');
  const [leagueData, setLeagueData] = useState<LeagueData>(loadLeagueData());
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPointsInput, setShowPointsInput] = useState(false);
  const [selectedPointsWeek, setSelectedPointsWeek] = useState(1);
  const [pointsType, setPointsType] = useState<'individual' | 'team'>('individual');
  const [showResetWeekConfirm, setShowResetWeekConfirm] = useState(false);
  const [resetWeekNumber, setResetWeekNumber] = useState(0);
  const [resetPasscode, setResetPasscode] = useState('');
  const [showTeamScoreInput, setShowTeamScoreInput] = useState(false);
  const [selectedTeamWeek, setSelectedTeamWeek] = useState(1);
  const [selectedPairing, setSelectedPairing] = useState<{ player1: string; player2: string } | null>(null);

  // Delete score states
  const [showDeleteScoreConfirm, setShowDeleteScoreConfirm] = useState(false);
  const [deleteScorePasscode, setDeleteScorePasscode] = useState('');

  // Results states
  const [resultsData, setResultsData] = useState<Record<string, RoundResults>>(loadResultsData());
  const [selectedResultsRound, setSelectedResultsRound] = useState('1-2');
  const [showAddIndResult, setShowAddIndResult] = useState(false);
  const [showAddTeamResult, setShowAddTeamResult] = useState(false);
  const [resultPasscode, setResultPasscode] = useState('');
  const [resultPasscodeVerified, setResultPasscodeVerified] = useState(false);
  const [resultPendingAction, setResultPendingAction] = useState<'addInd' | 'addTeam' | 'delete' | null>(null);
  const [showResultPasscode, setShowResultPasscode] = useState(false);
  const [deleteResultTarget, setDeleteResultTarget] = useState<{ type: 'individual' | 'team'; roundKey: string; index: number } | null>(null);

  // Individual result form
  const [indResultForm, setIndResultForm] = useState<IndividualResult>({
    place: '1st', playerId: '', playerName: '', netScore: '', totalIndividualPoints: ''
  });

  // Team result form
  const [teamResultForm, setTeamResultForm] = useState<TeamResult>({
    place: '1st', player1Id: '', player1Name: '', player2Id: '', player2Name: '',
    player1Score: '', player2Score: '', teamTotalScore: '', teamTotalPoints: ''
  });

  const biWeeklyRounds = [
    { key: '1-2', label: 'Weeks 1 & 2' },
    { key: '3-4', label: 'Weeks 3 & 4' },
    { key: '5-6', label: 'Weeks 5 & 6' },
    { key: '7-8', label: 'Weeks 7 & 8' },
    { key: '9-10', label: 'Weeks 9 & 10' },
    { key: '11-12', label: 'Weeks 11 & 12' },
  ];

  // Countdown timer for next match
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextMatch = getNextMatchDate();
      
      const diff = nextMatch.getTime() - now.getTime();
      
      if (diff < 0) {
        setTimeToNextMatch('Season Complete');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeToNextMatch(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const players = Object.values(leagueData.players);

  // Get players playing in a specific week
  const getPlayersForWeek = (week: number): Player[] => {
    const weekData = weekSchedule.find(w => w.week === week);
    if (!weekData) return [];
    
    const playingIds = weekData.pairings.flatMap(p => [p.player1, p.player2]);
    return players.filter(p => playingIds.includes(p.id));
  };

  // Get bi-weekly pairing range (weeks 1-2, 3-4, etc.)
  const getBiWeeklyRange = (week: number): number[] => {
    if (week <= 2) return [1, 2];
    if (week <= 4) return [3, 4];
    if (week <= 6) return [5, 6];
    if (week <= 8) return [7, 8];
    if (week <= 10) return [9, 10];
    if (week <= 12) return [11, 12];
    return [13];
  };

  // Get all pairings for a bi-weekly range
  const getBiWeeklyPairings = (week: number) => {
    const range = getBiWeeklyRange(week);
    const allPairings: Array<{ player1: string; player2: string; week: number }> = [];
    
    range.forEach(w => {
      const weekData = weekSchedule.find(ws => ws.week === w);
      if (weekData) {
        weekData.pairings.forEach(p => {
          allPairings.push({ ...p, week: w });
        });
      }
    });
    
    return allPairings;
  };

  const getPlayerTotalPoints = (playerId: string): number => {
    const playerPoints = leagueData.individualPoints[playerId] || {};
    return Object.values(playerPoints).reduce((sum, pts) => sum + pts, 0);
  };

  const getTeamTotalPoints = (teamName: string): number => {
    const teamPoints = leagueData.teamPoints[teamName] || {};
    return Object.values(teamPoints).reduce((sum, pts) => sum + pts, 0);
  };

  const getPlayerNetScore = (playerId: string, week: number): number | null => {
    const score = leagueData.scores[playerId]?.[week];
    return score ? score.net : null;
  };

  const getPairingCombinedScore = (pairing: { player1: string; player2: string }, week: number): number | null => {
    const score1 = leagueData.scores[pairing.player1]?.[week];
    const score2 = leagueData.scores[pairing.player2]?.[week];
    
    if (score1 && score2) {
      return score1.net + score2.net;
    }
    return null;
  };

  const getPairingTeamPoints = (pairing: { player1: string; player2: string }, week: number): number => {
    const points1 = leagueData.individualPoints[pairing.player1]?.[week] || 0;
    const points2 = leagueData.individualPoints[pairing.player2]?.[week] || 0;
    return points1 + points2;
  };

  const handleScoreClick = (player: Player) => {
    setSelectedPlayer(player);
    setSelectedWeek(selectedPointsWeek);
    setShowScoreInput(true);
    setPasscode('');
    setIsPasscodeVerified(false);
    setGrossScore('');
    setNetScore('');
  };

  const verifyPasscode = () => {
    if (passcode === 'mbc909') {
      setIsPasscodeVerified(true);
      // Load existing score if any
      const existingScore = leagueData.scores[selectedPlayer!.id]?.[selectedWeek];
      if (existingScore) {
        setGrossScore(existingScore.gross.toString());
        setNetScore(existingScore.net.toString());
      }
    } else {
      alert('Incorrect passcode');
    }
  };

  const saveScore = () => {
    if (!selectedPlayer || !grossScore || !netScore) {
      alert('Please enter both gross and net scores');
      return;
    }

    const newData = { ...leagueData };
    
    if (!newData.scores[selectedPlayer.id]) {
      newData.scores[selectedPlayer.id] = {};
    }
    
    newData.scores[selectedPlayer.id][selectedWeek] = {
      gross: parseInt(grossScore),
      net: parseInt(netScore)
    };
    
    saveLeagueData(newData);
    setLeagueData(newData);
    setShowScoreInput(false);
    setIsPasscodeVerified(false);
  };

  const handleDeleteScore = () => {
    setDeleteScorePasscode('');
    setShowDeleteScoreConfirm(true);
  };

  const confirmDeleteScore = () => {
    if (deleteScorePasscode !== 'mbc909') {
      alert('Incorrect passcode');
      return;
    }
    if (!selectedPlayer) return;

    const newData = { ...leagueData };
    if (newData.scores[selectedPlayer.id]?.[selectedWeek]) {
      delete newData.scores[selectedPlayer.id][selectedWeek];
      if (Object.keys(newData.scores[selectedPlayer.id]).length === 0) {
        delete newData.scores[selectedPlayer.id];
      }
    }
    saveLeagueData(newData);
    setLeagueData(newData);
    setShowDeleteScoreConfirm(false);
    setShowScoreInput(false);
    setIsPasscodeVerified(false);
  };

  const handleResetWeek = (week: number) => {
    setResetWeekNumber(week);
    setResetPasscode('');
    setShowResetWeekConfirm(true);
  };

  const confirmResetWeek = () => {
    if (resetPasscode === 'mbc909') {
      const newData = resetWeek(leagueData, resetWeekNumber);
      saveLeagueData(newData);
      setLeagueData(newData);
      setShowResetWeekConfirm(false);
      setResetPasscode('');
    } else {
      alert('Incorrect passcode');
    }
  };

  const handleResetAll = () => {
    setResetPasscode('');
    setShowResetConfirm(true);
  };

  const confirmResetAll = () => {
    if (resetPasscode === 'mbc909') {
      resetAllData();
      setLeagueData(loadLeagueData());
      setShowResetConfirm(false);
      setResetPasscode('');
    } else {
      alert('Incorrect passcode');
    }
  };

  const openPointsInput = (week: number, type: 'individual' | 'team') => {
    setSelectedPointsWeek(week);
    setPointsType(type);
    setPasscode('');
    setIsPasscodeVerified(false);
    setShowPointsInput(true);
  };

  const verifyPointsPasscode = () => {
    if (passcode === 'mbc909') {
      setIsPasscodeVerified(true);
    } else {
      alert('Incorrect passcode');
    }
  };

  const savePoints = (entityId: string, points: number) => {
    const newData = { ...leagueData };
    
    if (pointsType === 'individual') {
      if (!newData.individualPoints[entityId]) {
        newData.individualPoints[entityId] = {};
      }
      newData.individualPoints[entityId][selectedPointsWeek] = points;
    } else {
      if (!newData.teamPoints[entityId]) {
        newData.teamPoints[entityId] = {};
      }
      newData.teamPoints[entityId][selectedPointsWeek] = points;
    }
    
    saveLeagueData(newData);
    setLeagueData(newData);
  };

  const handleTeamScoreClick = (pairing: { player1: string; player2: string }, week: number) => {
    setSelectedPairing(pairing);
    setSelectedTeamWeek(week);
    setShowTeamScoreInput(true);
  };

  const saveTeamScore = (combinedNet: number, teamPoints: number) => {
    if (!selectedPairing) return;
    
    const newData = { ...leagueData };
    const player1 = players.find(p => p.id === selectedPairing.player1);
    const player2 = players.find(p => p.id === selectedPairing.player2);
    
    if (player1 && player2) {
      if (!newData.teamPoints[player1.teamName]) {
        newData.teamPoints[player1.teamName] = {};
      }
      if (!newData.teamPoints[player2.teamName]) {
        newData.teamPoints[player2.teamName] = {};
      }
      
      newData.teamPoints[player1.teamName][selectedTeamWeek] = teamPoints;
      newData.teamPoints[player2.teamName][selectedTeamWeek] = teamPoints;
    }
    
    saveLeagueData(newData);
    setLeagueData(newData);
    setShowTeamScoreInput(false);
  };

  const scrollToStandings = () => {
    const standingsSection = document.getElementById('standings-section');
    if (standingsSection) {
      standingsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Results functions
  const requestResultPasscode = (action: 'addInd' | 'addTeam' | 'delete') => {
    setResultPendingAction(action);
    setResultPasscode('');
    setShowResultPasscode(true);
  };

  const verifyResultPasscode = () => {
    if (resultPasscode === 'mbc909') {
      setResultPasscodeVerified(true);
      setShowResultPasscode(false);
      if (resultPendingAction === 'addInd') {
        setIndResultForm({ place: '1st', playerId: players[0]?.id || '', playerName: players[0]?.name || '', netScore: '', totalIndividualPoints: '' });
        setShowAddIndResult(true);
      } else if (resultPendingAction === 'addTeam') {
        setTeamResultForm({ place: '1st', player1Id: players[0]?.id || '', player1Name: players[0]?.name || '', player2Id: players[1]?.id || '', player2Name: players[1]?.name || '', player1Score: '', player2Score: '', teamTotalScore: '', teamTotalPoints: '' });
        setShowAddTeamResult(true);
      } else if (resultPendingAction === 'delete' && deleteResultTarget) {
        performDeleteResult();
      }
    } else {
      alert('Incorrect passcode');
    }
  };

  const performDeleteResult = () => {
    if (!deleteResultTarget) return;
    const newData = { ...resultsData };
    const round = newData[deleteResultTarget.roundKey];
    if (!round) return;
    if (deleteResultTarget.type === 'individual') {
      round.individuals.splice(deleteResultTarget.index, 1);
    } else {
      round.teams.splice(deleteResultTarget.index, 1);
    }
    if (round.individuals.length === 0 && round.teams.length === 0) {
      delete newData[deleteResultTarget.roundKey];
    }
    saveResultsData(newData);
    setResultsData(newData);
    setDeleteResultTarget(null);
  };

  const handleDeleteResult = (type: 'individual' | 'team', roundKey: string, index: number) => {
    setDeleteResultTarget({ type, roundKey, index });
    requestResultPasscode('delete');
  };

  const saveIndividualResult = () => {
    if (!indResultForm.playerId) {
      alert('Please select a player');
      return;
    }
    const newData = { ...resultsData };
    if (!newData[selectedResultsRound]) {
      newData[selectedResultsRound] = { individuals: [], teams: [] };
    }
    newData[selectedResultsRound].individuals.push({ ...indResultForm });
    newData[selectedResultsRound].individuals.sort((a, b) => {
      const order = { '1st': 1, '2nd': 2, '3rd': 3 };
      return order[a.place] - order[b.place];
    });
    saveResultsData(newData);
    setResultsData(newData);
    setShowAddIndResult(false);
    setResultPasscodeVerified(false);
  };

  const saveTeamResult = () => {
    if (!teamResultForm.player1Id || !teamResultForm.player2Id) {
      alert('Please select both players');
      return;
    }
    if (teamResultForm.player1Id === teamResultForm.player2Id) {
      alert('Please select two different players');
      return;
    }
    const newData = { ...resultsData };
    if (!newData[selectedResultsRound]) {
      newData[selectedResultsRound] = { individuals: [], teams: [] };
    }
    newData[selectedResultsRound].teams.push({ ...teamResultForm });
    newData[selectedResultsRound].teams.sort((a, b) => {
      const order: Record<string, number> = { '1st': 1, '2nd': 2 };
      return (order[a.place] || 99) - (order[b.place] || 99);
    });
    saveResultsData(newData);
    setResultsData(newData);
    setShowAddTeamResult(false);
    setResultPasscodeVerified(false);
  };

  // Sort players by total points
  const sortedPlayers = [...players].sort((a, b) => {
    return getPlayerTotalPoints(b.id) - getPlayerTotalPoints(a.id);
  });

  // Get players for selected week only
  const weekPlayers = getPlayersForWeek(selectedPointsWeek);
  const sortedWeekPlayers = [...weekPlayers].sort((a, b) => {
    return getPlayerTotalPoints(b.id) - getPlayerTotalPoints(a.id);
  });

  // Get unique teams and their total points
  const teams = Array.from(new Set(players.map(p => p.teamName))).map(teamName => ({
    teamName,
    totalPoints: getTeamTotalPoints(teamName)
  })).sort((a, b) => b.totalPoints - a.totalPoints);

  // Generate upcoming matches from weekSchedule
  const upcomingMatches = weekSchedule.slice(0, 13).map(week => {
    const pairingsText = week.pairings.map(p => {
      const p1 = players.find(pl => pl.id === p.player1);
      const p2 = players.find(pl => pl.id === p.player2);
      return `${p1?.name || p.player1} & ${p2?.name || p.player2}`;
    });

    return {
      week: `Week ${week.week}`,
      date: new Date(week.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: '5:30 PM PST',
      course: week.course,
      pairings: pairingsText,
    };
  });

  const currentRoundResults = resultsData[selectedResultsRound];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <div className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7a4c38700760832db255166e6555df42.jpg"
            alt="Golf Simulator"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        </div>
        
        <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6">
          <div className="text-center">
            <h1 className="text-4xl font-black text-white mb-2 tracking-tight leading-tight">
              MAGNIFICENT<br />BASTARDS CLUB
            </h1>
            <p className="text-emerald-400 text-lg font-bold mb-6">SEASON 2025-26</p>
            
            {/* Countdown Timer */}
            <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-4 mb-6">
              <div className="text-emerald-400 text-sm font-bold mb-1">NEXT MATCH IN</div>
              <div className="text-white text-2xl font-black">{timeToNextMatch}</div>
              <div className="text-slate-300 text-xs mt-1">Thursdays &bull; 5:30 PM PST</div>
            </div>
            
            <div className="flex gap-3 justify-center">
              <button className="bg-emerald-500 text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-emerald-600 transition-colors">
                VIEW SCHEDULE
              </button>
              <button 
                onClick={scrollToStandings}
                className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full font-bold text-sm border border-white/20 hover:bg-white/20 transition-colors"
              >
                STANDINGS
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-slate-800 px-6 py-6 border-b border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-black text-white">MAGNIFICENT BASTARDS</h1>
            <p className="text-emerald-400 text-sm font-bold">GOLF SIMULATOR LEAGUE</p>
          </div>
          <div className="flex gap-2">
            <Link 
              to="/totals" 
              className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
            >
              <i className="ri-trophy-line text-lg"></i>
            </Link>
            <Link 
              to="/scorecard" 
              className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
            >
              <i className="ri-table-line text-lg"></i>
            </Link>
            <Link 
              to="/money" 
              className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-full text-white hover:bg-yellow-600 transition-colors"
            >
              <i className="ri-money-dollar-circle-line text-lg"></i>
            </Link>
            <Link 
              to="/courses" 
              className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
            >
              <i className="ri-map-pin-line text-lg"></i>
            </Link>
            <Link 
              to="/rules" 
              className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
            >
              <i className="ri-book-line text-lg"></i>
            </Link>
            <Link 
              to="/calendar" 
              className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
            >
              <i className="ri-calendar-line text-lg"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-800 py-6 px-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-black text-emerald-400">12</div>
            <div className="text-xs text-slate-400 font-semibold mt-1">PLAYERS</div>
          </div>
          <div>
            <div className="text-3xl font-black text-emerald-400">12</div>
            <div className="text-xs text-slate-400 font-semibold mt-1">WEEKS</div>
          </div>
          <div>
            <div className="text-3xl font-black text-emerald-400">6</div>
            <div className="text-xs text-slate-400 font-semibold mt-1">PLAYING</div>
          </div>
        </div>
      </div>

      {/* Admin Controls */}
      <div className="bg-slate-800 px-6 py-4 border-b border-slate-700">
        <div className="flex gap-2 justify-center">
          <button
            onClick={handleResetAll}
            className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg text-xs font-bold border border-red-500/30 hover:bg-red-500/30 transition-colors"
          >
            RESET SEASON
          </button>
        </div>
      </div>

      {/* Standings Section */}
      <div id="standings-section" className="px-6 py-12 bg-slate-900">
        <div className="mb-6">
          <h2 className="text-3xl font-black text-white mb-4">STANDINGS</h2>
          <div className="flex gap-2 bg-slate-800 p-1 rounded-full mb-4">
            <button
              onClick={() => setActiveTab('individual')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
                activeTab === 'individual'
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              INDIVIDUAL
            </button>
            <button
              onClick={() => setActiveTab('pairings')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
                activeTab === 'pairings'
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              PAIRINGS
            </button>
          </div>
          
          {/* Week Selector */}
          <div className="mb-4">
            <label className="text-slate-400 text-xs font-semibold block mb-2">SELECT WEEK</label>
            <select
              value={selectedPointsWeek}
              onChange={(e) => setSelectedPointsWeek(parseInt(e.target.value))}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white"
            >
              {weekSchedule.map((week) => (
                <option key={week.week} value={week.week}>
                  Week {week.week} - {week.date} - {week.course}
                </option>
              ))}
            </select>
          </div>
        </div>

        {activeTab === 'individual' ? (
          <div className="space-y-3">
            {sortedWeekPlayers.map((player, index) => {
              const weekScore = getPlayerNetScore(player.id, selectedPointsWeek);
              const weekPoints = leagueData.individualPoints[player.id]?.[selectedPointsWeek] || 0;
              const totalPoints = getPlayerTotalPoints(player.id);
              
              return (
                <div
                  key={player.id}
                  className="bg-slate-800 rounded-2xl p-4 flex items-center gap-4 border border-slate-700/50"
                >
                  <div className="text-2xl font-black text-emerald-400 w-8">
                    {index + 1}
                  </div>
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-700 flex-shrink-0">
                    <img
                      src={player.image}
                      alt={player.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold text-sm">{player.name}</div>
                    <div className="text-slate-400 text-xs font-semibold">{player.teamName}</div>
                    <div className="text-emerald-400 text-xs font-bold mt-0.5">HCP: {player.handicap}</div>
                    {weekScore !== null && (
                      <div className="text-blue-400 text-xs mt-0.5">W{selectedPointsWeek} Net: {weekScore}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-white font-black text-lg">{totalPoints}</div>
                    <div className="text-slate-400 text-xs font-semibold">TOTAL PTS</div>
                    <div className="text-emerald-400 text-xs font-bold mt-1">W{selectedPointsWeek}: {weekPoints} pts</div>
                    <div className="flex gap-1 mt-2">
                      <button
                        onClick={() => handleScoreClick(player)}
                        className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold hover:bg-emerald-600 transition-colors"
                      >
                        SCORE
                      </button>
                      <button
                        onClick={() => openPointsInput(selectedPointsWeek, 'individual')}
                        className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold hover:bg-blue-600 transition-colors"
                      >
                        POINTS
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => handleResetWeek(selectedPointsWeek)}
                className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg text-xs font-bold border border-red-500/30 hover:bg-red-500/30 transition-colors"
              >
                RESET WEEK {selectedPointsWeek}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const biWeeklyRange = getBiWeeklyRange(selectedPointsWeek);
              const allPairings = getBiWeeklyPairings(selectedPointsWeek);
              const weekData = weekSchedule.find(w => w.week === biWeeklyRange[0]);
              
              return (
                <>
                  <div className="bg-slate-800 rounded-xl p-4 mb-4">
                    <div className="text-emerald-400 font-black text-sm mb-2">
                      WEEKS {biWeeklyRange[0]}-{biWeeklyRange[biWeeklyRange.length - 1]}
                    </div>
                    <div className="text-white text-xs">
                      {weekData?.date} - {weekData?.course}
                    </div>
                  </div>
                  
                  {allPairings.map((pairing, index) => {
                    const p1 = players.find(p => p.id === pairing.player1);
                    const p2 = players.find(p => p.id === pairing.player2);
                    const combinedScore = getPairingCombinedScore(pairing, pairing.week);
                    const teamPoints = getPairingTeamPoints(pairing, pairing.week);
                    const score1 = leagueData.scores[pairing.player1]?.[pairing.week];
                    const score2 = leagueData.scores[pairing.player2]?.[pairing.week];
                    
                    return (
                      <div
                        key={`${pairing.week}-${index}`}
                        className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-emerald-400 font-black text-sm">
                            WEEK {pairing.week} - PAIRING {(index % 3) + 1}
                          </div>
                          {combinedScore !== null && (
                            <div className="text-white font-black text-lg">NET: {combinedScore}</div>
                          )}
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                            <div>
                              <div className="text-white font-bold text-sm">{p1?.name}</div>
                              <div className="text-slate-400 text-xs">{p1?.teamName}</div>
                            </div>
                            {score1 && (
                              <div className="text-right">
                                <div className="text-emerald-400 font-bold text-sm">Net: {score1.net}</div>
                                <div className="text-blue-400 text-xs">{leagueData.individualPoints[pairing.player1]?.[pairing.week] || 0} pts</div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                            <div>
                              <div className="text-white font-bold text-sm">{p2?.name}</div>
                              <div className="text-slate-400 text-xs">{p2?.teamName}</div>
                            </div>
                            {score2 && (
                              <div className="text-right">
                                <div className="text-emerald-400 font-bold text-sm">Net: {score2.net}</div>
                                <div className="text-blue-400 text-xs">{leagueData.individualPoints[pairing.player2]?.[pairing.week] || 0} pts</div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/30">
                          <div className="text-emerald-400 font-bold text-sm">TEAM POINTS</div>
                          <div className="text-emerald-400 font-black text-lg">{teamPoints} pts</div>
                        </div>
                        
                        <button
                          onClick={() => handleTeamScoreClick(pairing, pairing.week)}
                          className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-bold mt-3 hover:bg-blue-600 transition-colors"
                        >
                          EDIT TEAM SCORE
                        </button>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Schedule Section */}
      <div className="px-6 py-12 bg-slate-800">
        <div className="mb-6">
          <h2 className="text-3xl font-black text-white mb-4">SCHEDULE</h2>
          <div className="flex gap-2 bg-slate-900 p-1 rounded-full">
            <button
              onClick={() => setScheduleFilter('upcoming')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
                scheduleFilter === 'upcoming'
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              UPCOMING
            </button>
            <button
              onClick={() => setScheduleFilter('completed')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
                scheduleFilter === 'completed'
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              RESULTS
            </button>
          </div>
        </div>

        {scheduleFilter === 'upcoming' ? (
          <div className="space-y-4">
            {upcomingMatches.map((match, index) => (
              <div
                key={index}
                className="bg-slate-900 rounded-2xl p-5 border border-slate-700/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-emerald-400 font-black text-sm">{match.week}</div>
                  <div className="text-slate-400 text-xs font-semibold">{match.date} &bull; {match.time}</div>
                </div>
                <div className="text-center mb-3">
                  <div className="text-white font-bold text-xs bg-slate-800 rounded-lg py-2 px-3">
                    {match.course}
                  </div>
                </div>
                <div className="space-y-2">
                  {match.pairings.length > 0 ? (
                    match.pairings.map((pairing, pIdx) => (
                      <div key={pIdx} className="bg-slate-800 rounded-lg p-3">
                        <div className="text-white font-bold text-sm">{pairing}</div>
                      </div>
                    ))
                  ) : (
                    <div className="bg-slate-800 rounded-lg p-3">
                      <div className="text-slate-400 font-bold text-sm">Playoffs - TBD Pairings</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Round Selector */}
            <div className="mb-4">
              <label className="text-slate-400 text-xs font-semibold block mb-2">SELECT ROUND</label>
              <div className="relative">
                <select
                  value={selectedResultsRound}
                  onChange={(e) => setSelectedResultsRound(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm appearance-none"
                >
                  {biWeeklyRounds.map((round) => (
                    <option key={round.key} value={round.key}>{round.label}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-slate-400"></i>
                </div>
              </div>
            </div>

            {/* Individual Results */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-black text-sm">
                  <i className="ri-user-star-line text-emerald-400 mr-1"></i>
                  INDIVIDUAL RESULTS
                </h3>
                <button
                  onClick={() => requestResultPasscode('addInd')}
                  className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold hover:bg-emerald-600 transition-colors"
                >
                  +
                </button>
              </div>

              {currentRoundResults?.individuals && currentRoundResults.individuals.length > 0 ? (
                <div className="space-y-2">
                  {currentRoundResults.individuals.map((result, idx) => (
                    <div key={idx} className="bg-slate-900 rounded-xl p-4 border border-slate-700/50 relative">
                      <button
                        onClick={() => handleDeleteResult('individual', selectedResultsRound, idx)}
                        className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="text-xl">
                          {result.place === '1st' ? '🥇' : result.place === '2nd' ? '🥈' : '🥉'}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-black text-sm">{result.playerName}</div>
                          <div className="text-slate-400 text-xs">{result.place} Place Individual</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 bg-slate-800 rounded-lg p-3">
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">NET SCORE</div>
                          <div className="text-white font-bold text-sm">{result.netScore || '-'}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">IND POINTS</div>
                          <div className="text-emerald-400 font-bold text-sm">{result.totalIndividualPoints || '-'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-900 rounded-xl p-6 text-center border border-slate-700/50">
                  <div className="text-slate-500 text-sm">No individual results yet</div>
                  <div className="text-slate-600 text-xs mt-1">Tap + to add 1st, 2nd, 3rd place</div>
                </div>
              )}
            </div>

            {/* Team Results */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-black text-sm">
                  <i className="ri-team-line text-emerald-400 mr-1"></i>
                  TEAM RESULTS
                </h3>
                <button
                  onClick={() => requestResultPasscode('addTeam')}
                  className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold hover:bg-emerald-600 transition-colors"
                >
                  +
                </button>
              </div>

              {currentRoundResults?.teams && currentRoundResults.teams.length > 0 ? (
                <div className="space-y-2">
                  {currentRoundResults.teams.map((team, idx) => (
                    <div key={idx} className="bg-slate-900 rounded-xl p-4 border border-slate-700/50 relative">
                      <button
                        onClick={() => handleDeleteResult('team', selectedResultsRound, idx)}
                        className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-xl">
                          {team.place === '1st' ? '🥇' : '🥈'}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-black text-sm">{team.player1Name} & {team.player2Name}</div>
                          <div className="text-slate-400 text-xs">{team.place} Place Team</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 bg-slate-800 rounded-lg p-3 mb-2">
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">{team.player1Name.split(' ')[0]}</div>
                          <div className="text-white font-bold text-sm">Net: {team.player1Score || '-'}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">{team.player2Name.split(' ')[0]}</div>
                          <div className="text-white font-bold text-sm">Net: {team.player2Score || '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 bg-slate-800 rounded-lg p-3">
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">TEAM TOTAL</div>
                          <div className="text-blue-400 font-bold text-sm">{team.teamTotalScore || '-'}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-slate-500 text-[10px] font-semibold">TEAM PTS</div>
                          <div className="text-emerald-400 font-bold text-sm">{team.teamTotalPoints || '-'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-900 rounded-xl p-6 text-center border border-slate-700/50">
                  <div className="text-slate-500 text-sm">No team results yet</div>
                  <div className="text-slate-600 text-xs mt-1">Tap + to add 1st &amp; 2nd place teams</div>
                </div>
              )}
            </div>

            {/* All Rounds Quick View */}
            <div className="mt-6">
              <h3 className="text-white font-black text-sm mb-3">ALL ROUNDS</h3>
              <div className="space-y-2">
                {biWeeklyRounds.map((round) => {
                  const data = resultsData[round.key];
                  const hasData = data && (data.individuals.length > 0 || data.teams.length > 0);
                  return (
                    <button
                      key={round.key}
                      onClick={() => setSelectedResultsRound(round.key)}
                      className={`w-full text-left bg-slate-900 rounded-lg p-3 border transition-all ${
                        selectedResultsRound === round.key
                          ? 'border-emerald-500/50 bg-emerald-500/5'
                          : 'border-slate-700/50 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-white font-bold text-sm">{round.label}</div>
                        {hasData ? (
                          <div className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full text-[10px] font-bold">
                            ENTERED
                          </div>
                        ) : (
                          <div className="bg-slate-700/50 text-slate-500 px-2 py-0.5 rounded-full text-[10px] font-bold">
                            PENDING
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Score Input Modal */}
      {showScoreInput && selectedPlayer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">
              Enter Score for {selectedPlayer.name}
            </h3>
            
            {!isPasscodeVerified ? (
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Enter Passcode
                  </label>
                  <input
                    type="password"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                    placeholder="Enter passcode"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={verifyPasscode}
                    className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                  >
                    VERIFY
                  </button>
                  <button
                    onClick={() => setShowScoreInput(false)}
                    className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Select Week
                  </label>
                  <select
                    value={selectedWeek}
                    onChange={(e) => {
                      const week = parseInt(e.target.value);
                      setSelectedWeek(week);
                      const existingScore = leagueData.scores[selectedPlayer.id]?.[week];
                      if (existingScore) {
                        setGrossScore(existingScore.gross.toString());
                        setNetScore(existingScore.net.toString());
                      } else {
                        setGrossScore('');
                        setNetScore('');
                      }
                    }}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                  >
                    {weekSchedule.map((week) => (
                      <option key={week.week} value={week.week}>
                        Week {week.week} - {week.course}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Gross Score (HCP: {selectedPlayer.handicap})
                  </label>
                  <input
                    type="number"
                    value={grossScore}
                    onChange={(e) => setGrossScore(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                    placeholder="Enter gross score"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Net Score (Manual Entry)
                  </label>
                  <input
                    type="number"
                    value={netScore}
                    onChange={(e) => setNetScore(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                    placeholder="Enter net score"
                  />
                </div>

                {/* Existing score indicator */}
                {leagueData.scores[selectedPlayer.id]?.[selectedWeek] && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="text-yellow-400 text-xs font-bold mb-1">EXISTING SCORE FOR WEEK {selectedWeek}</div>
                    <div className="text-white text-sm">
                      Gross: {leagueData.scores[selectedPlayer.id][selectedWeek].gross} | Net: {leagueData.scores[selectedPlayer.id][selectedWeek].net}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={saveScore}
                    className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                  >
                    SAVE SCORE
                  </button>
                  <button
                    onClick={() => setShowScoreInput(false)}
                    className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
                  >
                    CANCEL
                  </button>
                </div>

                {/* Delete Score Button */}
                {leagueData.scores[selectedPlayer.id]?.[selectedWeek] && (
                  <button
                    onClick={handleDeleteScore}
                    className="w-full bg-red-500/20 text-red-400 py-3 rounded-lg font-bold border border-red-500/30 hover:bg-red-500/30 transition-colors text-sm"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    DELETE SCORE FOR WEEK {selectedWeek}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Score Confirmation Modal */}
      {showDeleteScoreConfirm && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-red-500/30">
            <h3 className="text-white font-black text-lg mb-2">Delete Score?</h3>
            <p className="text-slate-300 text-sm mb-4">
              This will permanently delete {selectedPlayer?.name}&apos;s score for Week {selectedWeek}.
            </p>
            <div className="mb-4">
              <label className="text-slate-400 text-sm font-semibold block mb-2">Enter Passcode to Confirm</label>
              <input
                type="password"
                value={deleteScorePasscode}
                onChange={(e) => setDeleteScorePasscode(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                placeholder="Enter passcode"
                onKeyDown={(e) => e.key === 'Enter' && confirmDeleteScore()}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteScore}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold hover:bg-red-600 transition-colors"
              >
                DELETE
              </button>
              <button
                onClick={() => setShowDeleteScoreConfirm(false)}
                className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Score Input Modal */}
      {showTeamScoreInput && selectedPairing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">
              Edit Team Score - Week {selectedTeamWeek}
            </h3>
            
            <div className="space-y-4">
              <div className="bg-slate-900 rounded-lg p-3 mb-4">
                <div className="text-slate-400 text-xs mb-2">PAIRING</div>
                <div className="text-white font-bold text-sm">
                  {players.find(p => p.id === selectedPairing.player1)?.name} & {players.find(p => p.id === selectedPairing.player2)?.name}
                </div>
                <div className="text-emerald-400 text-xs mt-1">
                  Combined Net: {getPairingCombinedScore(selectedPairing, selectedTeamWeek) || 'N/A'}
                </div>
              </div>
              
              <div>
                <label className="text-slate-400 text-sm font-semibold block mb-2">
                  Team Points (6 pts for 1st, etc.)
                </label>
                <input
                  type="number"
                  id="team-points-input"
                  defaultValue={getPairingTeamPoints(selectedPairing, selectedTeamWeek)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                  placeholder="Enter team points"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const input = document.getElementById('team-points-input') as HTMLInputElement;
                    const points = parseInt(input.value) || 0;
                    const combinedNet = getPairingCombinedScore(selectedPairing, selectedTeamWeek) || 0;
                    saveTeamScore(combinedNet, points);
                  }}
                  className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                >
                  SAVE
                </button>
                <button
                  onClick={() => setShowTeamScoreInput(false)}
                  className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Points Input Modal */}
      {showPointsInput && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[80vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-4">
              Enter {pointsType === 'individual' ? 'Individual' : 'Team'} Points - Week {selectedPointsWeek}
            </h3>
            
            {!isPasscodeVerified ? (
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Enter Passcode
                  </label>
                  <input
                    type="password"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                    placeholder="Enter passcode"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={verifyPointsPasscode}
                    className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                  >
                    VERIFY
                  </button>
                  <button
                    onClick={() => setShowPointsInput(false)}
                    className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {pointsType === 'individual' ? (
                  sortedPlayers.map((player) => {
                    const currentPoints = leagueData.individualPoints[player.id]?.[selectedPointsWeek] || 0;
                    return (
                      <div key={player.id} className="bg-slate-900 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white font-bold text-sm">{player.name}</div>
                          <div className="text-slate-400 text-xs">Current: {currentPoints}</div>
                        </div>
                        <input
                          type="number"
                          defaultValue={currentPoints}
                          onChange={(e) => savePoints(player.id, parseInt(e.target.value) || 0)}
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                          placeholder="Points"
                        />
                      </div>
                    );
                  })
                ) : (
                  teams.map((team) => {
                    const currentPoints = leagueData.teamPoints[team.teamName]?.[selectedPointsWeek] || 0;
                    return (
                      <div key={team.teamName} className="bg-slate-900 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white font-bold text-sm">{team.teamName}</div>
                          <div className="text-slate-400 text-xs">Current: {currentPoints}</div>
                        </div>
                        <input
                          type="number"
                          defaultValue={currentPoints}
                          onChange={(e) => savePoints(team.teamName, parseInt(e.target.value) || 0)}
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                          placeholder="Points"
                        />
                      </div>
                    );
                  })
                )}
                
                <button
                  onClick={() => {
                    setShowPointsInput(false);
                    setIsPasscodeVerified(false);
                  }}
                  className="w-full bg-emerald-500 text-white py-3 rounded-lg font-bold mt-4 hover:bg-emerald-600 transition-colors"
                >
                  DONE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Week Confirmation Modal */}
      {showResetWeekConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">
              Reset Week {resetWeekNumber}?
            </h3>
            <p className="text-slate-300 text-sm mb-4">
              This will delete all scores and points for Week {resetWeekNumber}. This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="text-slate-400 text-sm font-semibold block mb-2">
                Enter Passcode
              </label>
              <input
                type="password"
                value={resetPasscode}
                onChange={(e) => setResetPasscode(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                placeholder="Enter passcode"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmResetWeek}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold hover:bg-red-600 transition-colors"
              >
                YES, RESET
              </button>
              <button
                onClick={() => setShowResetWeekConfirm(false)}
                className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Season Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">
              Reset Entire Season?
            </h3>
            <p className="text-slate-300 text-sm mb-4">
              This will delete all scores and points for all weeks. This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="text-slate-400 text-sm font-semibold block mb-2">
                Enter Passcode
              </label>
              <input
                type="password"
                value={resetPasscode}
                onChange={(e) => setResetPasscode(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                placeholder="Enter passcode"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmResetAll}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold hover:bg-red-600 transition-colors"
              >
                YES, RESET
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Featured Image Section */}
      <div className="relative h-64 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20golf%20simulator%20setup%20with%20multiple%20screens%20showing%20golf%20course%2C%20modern%20indoor%20facility%20with%20green%20putting%20surface%2C%20dramatic%20blue%20and%20green%20lighting%2C%20high-tech%20sports%20venue%2C%20photorealistic%2C%20cinematic%20quality%2C%20wide%20angle%20view&width=375&height=256&seq=feature1&orientation=landscape"
          alt="Simulator"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
      </div>

      {/* Result Passcode Modal */}
      {showResultPasscode && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">Enter Passcode</h3>
            <input
              type="password"
              value={resultPasscode}
              onChange={(e) => setResultPasscode(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white mb-4"
              placeholder="Enter passcode"
              onKeyDown={(e) => e.key === 'Enter' && verifyResultPasscode()}
            />
            <div className="flex gap-3">
              <button onClick={verifyResultPasscode} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">VERIFY</button>
              <button onClick={() => { setShowResultPasscode(false); setDeleteResultTarget(null); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Individual Result Modal */}
      {showAddIndResult && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[85vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-1">Add Individual Result</h3>
            <p className="text-slate-400 text-xs mb-4">Weeks {selectedResultsRound}</p>

            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Place</label>
                <div className="flex gap-2">
                  {(['1st', '2nd', '3rd'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setIndResultForm({ ...indResultForm, place: p })}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                        indResultForm.place === p ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-slate-400 border border-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Player</label>
                <div className="relative">
                  <select
                    value={indResultForm.playerId}
                    onChange={(e) => {
                      const p = players.find(pl => pl.id === e.target.value);
                      setIndResultForm({ ...indResultForm, playerId: e.target.value, playerName: p?.name || '' });
                    }}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm appearance-none"
                  >
                    {sortedPlayers.map(player => (
                      <option key={player.id} value={player.id}>{player.name} ({player.teamName})</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-slate-400"></i>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Net Score</label>
                <input
                  type="number"
                  value={indResultForm.netScore}
                  onChange={(e) => setIndResultForm({ ...indResultForm, netScore: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="Enter net score"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Total Individual Points</label>
                <input
                  type="number"
                  value={indResultForm.totalIndividualPoints}
                  onChange={(e) => setIndResultForm({ ...indResultForm, totalIndividualPoints: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="e.g. 10"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveIndividualResult} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">SAVE</button>
                <button onClick={() => { setShowAddIndResult(false); setResultPasscodeVerified(false); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Team Result Modal */}
      {showAddTeamResult && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[85vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-1">Add Team Result</h3>
            <p className="text-slate-400 text-xs mb-4">Weeks {selectedResultsRound}</p>

            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Place</label>
                <div className="flex gap-2">
                  {(['1st', '2nd'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setTeamResultForm({ ...teamResultForm, place: p })}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                        teamResultForm.place === p ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-slate-400 border border-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Player 1</label>
                <div className="relative">
                  <select
                    value={teamResultForm.player1Id}
                    onChange={(e) => {
                      const p = players.find(pl => pl.id === e.target.value);
                      setTeamResultForm({ ...teamResultForm, player1Id: e.target.value, player1Name: p?.name || '' });
                    }}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm appearance-none"
                  >
                    {sortedPlayers.map(player => (
                      <option key={player.id} value={player.id}>{player.name} ({player.teamName})</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-slate-400"></i>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Player 2</label>
                <div className="relative">
                  <select
                    value={teamResultForm.player2Id}
                    onChange={(e) => {
                      const p = players.find(pl => pl.id === e.target.value);
                      setTeamResultForm({ ...teamResultForm, player2Id: e.target.value, player2Name: p?.name || '' });
                    }}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm appearance-none"
                  >
                    {sortedPlayers.map(player => (
                      <option key={player.id} value={player.id}>{player.name} ({player.teamName})</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-slate-400"></i>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">P1 NET SCORE</label>
                  <input
                    type="number"
                    value={teamResultForm.player1Score}
                    onChange={(e) => setTeamResultForm({ ...teamResultForm, player1Score: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">P2 NET SCORE</label>
                  <input
                    type="number"
                    value={teamResultForm.player2Score}
                    onChange={(e) => setTeamResultForm({ ...teamResultForm, player2Score: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">TEAM TOTAL SCORE</label>
                  <input
                    type="number"
                    value={teamResultForm.teamTotalScore}
                    onChange={(e) => setTeamResultForm({ ...teamResultForm, teamTotalScore: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">TEAM TOTAL PTS</label>
                  <input
                    type="number"
                    value={teamResultForm.teamTotalPoints}
                    onChange={(e) => setTeamResultForm({ ...teamResultForm, teamTotalPoints: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveTeamResult} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">SAVE</button>
                <button onClick={() => { setShowAddTeamResult(false); setResultPasscodeVerified(false); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-slate-900 px-6 py-12 text-center">
        <div className="text-white font-black text-2xl mb-2">MAGNIFICENT BASTARDS CLUB</div>
        <div className="text-slate-400 text-sm font-semibold mb-8">Season 2025-26</div>
        <div className="flex justify-center gap-6 mb-8">
          <a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">
            <i className="ri-instagram-line text-2xl"></i>
          </a>
          <a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">
            <i className="ri-twitter-x-line text-2xl"></i>
          </a>
          <a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">
            <i className="ri-facebook-circle-line text-2xl"></i>
          </a>
        </div>
        <div className="text-slate-500 text-xs">&copy; 2025 Magnificent Bastards Club. All rights reserved.</div>
      </div>
    </div>
  );
}
